import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { ProductAttributeSerializer } from "@app-contexts/dashboard/features/products/data/types";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";
import { UseModelServiceType } from "@shared/services/api/hooks/useModelService";
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import VenueBasedEventRepository from "@app-contexts/dashboard/features/events/data/repositories/VenueBasedEventRepository";
import PublicBasedMembershipGroupRepository from "@shared/modules/memberships/data/repositories/PublicBasedMembershipGroupRepository";
import VenueBasedMembershipGroupRepository from "@app-contexts/dashboard/features/memberships/data/repositories/VenueBasedMembershipGroupRepository";
import VenueBasedProductsRepository from "@app-contexts/dashboard/features/products/data/repositories/VenueBasedProductsRepository";
import PublicBasedProductsRepository from "@app-contexts/public/features/products/data/repositories/PublicBasedProductsRepository";
import useItemGroupEvent from "./useItemGroupEvent";
import useItemGroupProduct from "./useItemGroupProduct";
import useItemGroupMembershipGroup from "./useItemGroupMembershipGroup";

export default function useItemGroupPackage(
  itemGroup: TicketItemGroupType,
  eventService: () => UseModelServiceType<
    PublicBaseEventRepository | VenueBasedEventRepository
  >,
  membershipService: () => UseModelServiceType<
    PublicBasedMembershipGroupRepository | VenueBasedMembershipGroupRepository
  >,
  productService: () => UseModelServiceType<
    VenueBasedProductsRepository | PublicBasedProductsRepository
  >
) {
  const {
    eventData,
    isFetching: isFetchingEvent,
    ticketTypes,
  } = useItemGroupEvent(itemGroup.event, undefined, eventService, undefined);
  const { productData, isFetching: isFetchingProduct } = useItemGroupProduct(
    itemGroup.product,
    undefined,
    productService,
    undefined
  );
  const { groupData, isFetching: isFetchingMembership } =
    useItemGroupMembershipGroup(
      itemGroup.membership_group,
      undefined,
      membershipService,
      undefined
    );

  const name = eventData?.name || productData?.name || groupData?.name;
  const ticketType = (ticketTypes as TicketTypeSerializer[])?.find(
    (ticketType) => ticketType.id === itemGroup.type
  );
  const product = (
    productData?.product_attributes as ProductAttributeSerializer[]
  )?.find((pa) => itemGroup.product_attribute === pa.id);
  const membershipLevel = (
    groupData?.membership_levels as VenueBasedMembershipGroupSerializer["membership_levels"]
  )?.find((level) => level.id === itemGroup.membership_level);
  const seatPermissions = itemGroup.permissions ?? [];
  const locationPermissions = itemGroup.location_permissions ?? [];

  const hasSeats = seatPermissions.length > 0 || locationPermissions.length > 0;

  const subtitle = ticketType?.name || product?.name || membershipLevel?.name;

  const isLoading =
    isFetchingEvent || isFetchingProduct || isFetchingMembership;

  return {
    name,
    subtitle,
    hasSeats,
    productData,
    seatPermissions,
    locationPermissions,
    isLoading,
  };
}
